import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Cookie, Check, X, Settings } from 'lucide-react';

const CONSENT_KEY = 'germanatic_consent_v2';
const LEGACY_KEYS = [
  'germanatic_cookie_consent',
  'integro_cookie_consent',
  'talay_cookie_consent',
];

// Google Consent Mode v2 — passes the user's choice as a signal to AdSense.
// AdSense always loads (from index.html) and respects this signal:
//   granted → personalized ads + full cookies + profiling
//   denied  → non-personalized ads, no interest profiling; Google may still set
//             minimal identifiers/cookies for frequency capping, aggregated
//             reporting and fraud prevention (within legally permitted scope).
const updateConsentSignal = (granted) => {
  if (typeof window === 'undefined') return;
  window.dataLayer = window.dataLayer || [];
  function gtag(){ window.dataLayer.push(arguments); }
  const value = granted ? 'granted' : 'denied';
  gtag('consent', 'update', {
    ad_storage: value,
    ad_user_data: value,
    ad_personalization: value,
  });
};

export const getConsent = () => {
  if (typeof window === 'undefined') return null;
  try {
    return JSON.parse(localStorage.getItem(CONSENT_KEY) || 'null');
  } catch {
    return null;
  }
};

const CookieConsent = () => {
  const [showBanner, setShowBanner] = useState(false);
  const [showDetails, setShowDetails] = useState(false);
  const [marketing, setMarketing] = useState(false);

  useEffect(() => {
    // Migrate / detect existing consent
    const existing = getConsent();
    if (existing) {
      // Replay the saved signal so AdSense gets the right state on every navigation
      updateConsentSignal(!!existing.marketing);
    } else {
      // Don't auto-import old generic banners — those didn't ask about ads.
      LEGACY_KEYS.forEach((k) => localStorage.removeItem(k));
      setTimeout(() => setShowBanner(true), 400);
    }

    // Listen for the global "open cookie settings" event dispatched from the footer
    const onOpen = () => {
      const c = getConsent();
      setMarketing(!!(c && c.marketing));
      setShowDetails(false);
      setShowBanner(true);
    };
    window.addEventListener('open-cookie-settings', onOpen);
    return () => window.removeEventListener('open-cookie-settings', onOpen);
  }, []);

  const persist = (payload) => {
    localStorage.setItem(
      CONSENT_KEY,
      JSON.stringify({ ...payload, timestamp: new Date().toISOString() })
    );
  };

  const handleAcceptAll = () => {
    persist({ necessary: true, marketing: true });
    updateConsentSignal(true);
    setShowBanner(false);
  };

  const handleRejectAll = () => {
    persist({ necessary: true, marketing: false });
    updateConsentSignal(false);
    setShowBanner(false);
  };

  const handleSavePreferences = () => {
    persist({ necessary: true, marketing });
    updateConsentSignal(marketing);
    setShowBanner(false);
    setShowDetails(false);
  };

  if (!showBanner) return null;

  return (
    <div
      className="fixed inset-0 z-50 flex items-end justify-center p-4 pointer-events-none"
      role="dialog"
      aria-modal="true"
      aria-label="Cookie consent"
      data-testid="cookie-banner"
    >
      <div className="fixed inset-0 bg-black/40 pointer-events-auto" />

      <div className="relative w-full max-w-2xl bg-white rounded-2xl shadow-2xl pointer-events-auto mb-4 overflow-hidden border border-babyblue-200">
        {/* Header */}
        <div className="bg-gradient-to-r from-primary to-babyblue-400 px-6 py-4 flex items-center gap-3">
          <Cookie className="w-7 h-7 text-white" />
          <h2 className="text-lg font-bold text-white">
            Cookies & Werbung / Cookies & Advertising
          </h2>
        </div>

        <div className="p-6">
          {!showDetails ? (
            <>
              <p className="text-babyblue-700 mb-3 leading-relaxed text-sm">
                Wir verwenden technisch notwendige Speichermechanismen sowie — mit Ihrer
                Einwilligung — Cookies und Technologien von <strong>Google AdSense</strong>, um{' '}
                <strong>personalisierte Werbung</strong> anzuzeigen. Wenn Sie Ihre Einwilligung
                verweigern, werden ausschließlich <strong>nicht-personalisierte Anzeigen</strong>{' '}
                ausgespielt; hierbei werden keine Profile zu Ihrer Person gebildet (Google
                Consent Mode v2). Dennoch erforderliche Datenübermittlungen (z. B. zur
                Betrugsprävention oder Anzeigenzählung) erfolgen im gesetzlich zulässigen Rahmen.
                Sie können Ihre Auswahl jederzeit in unserer{' '}
                <Link to="/datenschutz" className="text-primary underline">
                  Datenschutzerklärung
                </Link>{' '}
                widerrufen.
              </p>
              <p className="text-babyblue-600 text-xs mb-5 leading-relaxed">
                We use technically necessary storage and — with your consent — cookies and
                technologies from <strong>Google AdSense</strong> to show{' '}
                <strong>personalised advertising</strong>. If you refuse your consent, only{' '}
                <strong>non-personalised ads</strong> are displayed; no profiles are formed about
                you (Google Consent Mode v2). Data transmissions that remain necessary (e.g. for
                fraud prevention or ad capping) occur within the legally permitted framework. You
                can revoke your choice at any time in our{' '}
                <Link to="/datenschutz" className="text-primary underline">
                  Privacy Policy
                </Link>
                .
              </p>

              <div className="flex flex-col sm:flex-row gap-3 mb-3">
                <button
                  onClick={handleAcceptAll}
                  className="flex-1 bg-primary text-white px-6 py-3 rounded-lg font-medium hover:bg-primary-hover transition-colors flex items-center justify-center gap-2"
                  data-testid="cookie-accept-all"
                >
                  <Check className="w-5 h-5" />
                  Alle akzeptieren / Accept all
                </button>
                <button
                  onClick={handleRejectAll}
                  className="flex-1 bg-white text-babyblue-800 border-2 border-babyblue-300 px-6 py-3 rounded-lg font-medium hover:border-primary hover:text-primary transition-colors flex items-center justify-center gap-2"
                  data-testid="cookie-reject-all"
                >
                  <X className="w-5 h-5" />
                  Alle ablehnen / Reject all
                </button>
              </div>
              <button
                onClick={() => setShowDetails(true)}
                className="w-full text-babyblue-600 hover:text-primary text-sm font-medium flex items-center justify-center gap-2 underline-offset-2 hover:underline"
                data-testid="cookie-customize"
              >
                <Settings className="w-4 h-4" />
                Einstellungen / Customize
              </button>
            </>
          ) : (
            <>
              <div className="space-y-3 mb-5">
                {/* Necessary */}
                <div className="border border-babyblue-200 rounded-lg p-4 bg-babyblue-50">
                  <div className="flex items-center justify-between mb-1">
                    <h3 className="font-semibold text-babyblue-900 text-sm">
                      Technisch notwendig / Strictly necessary
                    </h3>
                    <span className="bg-green-100 text-green-800 text-xs px-2 py-0.5 rounded-full">
                      Immer aktiv / Always on
                    </span>
                  </div>
                  <p className="text-xs text-babyblue-600 leading-relaxed">
                    Speichert lediglich Ihre Cookie-Auswahl in Ihrem Browser. Rechtsgrundlage:
                    § 25 Abs. 2 Nr. 2 TDDDG.
                  </p>
                  <p className="text-xs text-babyblue-500 italic mt-1">
                    Stores only your cookie choice in your browser. Legal basis: § 25 (2) No. 2
                    TDDDG.
                  </p>
                </div>

                {/* Marketing / AdSense */}
                <div className="border border-babyblue-200 rounded-lg p-4">
                  <div className="flex items-center justify-between mb-1">
                    <h3 className="font-semibold text-babyblue-900 text-sm">
                      Werbung / Advertising (Google AdSense)
                    </h3>
                    <label className="relative inline-flex items-center cursor-pointer">
                      <input
                        type="checkbox"
                        checked={marketing}
                        onChange={(e) => setMarketing(e.target.checked)}
                        className="sr-only peer"
                        data-testid="cookie-toggle-marketing"
                      />
                      <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-primary"></div>
                    </label>
                  </div>
                  <p className="text-xs text-babyblue-600 leading-relaxed">
                    Ermöglicht die Anzeige personalisierter und nicht-personalisierter Werbung
                    durch Google AdSense. Hierbei werden Cookies gesetzt und Daten an Google LLC
                    (USA) übermittelt. Rechtsgrundlage: Art. 6 Abs. 1 lit. a DSGVO (Einwilligung).
                  </p>
                  <p className="text-xs text-babyblue-500 italic mt-1">
                    Enables Google AdSense to show personalised and non-personalised ads. Cookies
                    are set and data is transmitted to Google LLC (USA). Legal basis: Art. 6 (1)
                    lit. a GDPR (consent).
                  </p>
                </div>
              </div>

              <div className="flex flex-col sm:flex-row gap-3">
                <button
                  onClick={() => setShowDetails(false)}
                  className="flex-1 bg-white text-babyblue-700 border-2 border-babyblue-300 px-6 py-3 rounded-lg font-medium hover:border-primary hover:text-primary transition-colors"
                  data-testid="cookie-back"
                >
                  Zurück / Back
                </button>
                <button
                  onClick={handleSavePreferences}
                  className="flex-1 bg-primary text-white px-6 py-3 rounded-lg font-medium hover:bg-primary-hover transition-colors flex items-center justify-center gap-2"
                  data-testid="cookie-save-preferences"
                >
                  <Check className="w-5 h-5" />
                  Auswahl speichern / Save choices
                </button>
              </div>
            </>
          )}

          <div className="flex flex-wrap gap-4 text-xs mt-5 pt-4 border-t border-babyblue-100">
            <Link to="/datenschutz" className="text-primary hover:underline">
              Datenschutzerklärung / Privacy Policy
            </Link>
            <Link to="/impressum" className="text-primary hover:underline">
              Impressum / Legal Notice
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CookieConsent;
